/**
 * Audio del módulo radio.
 *
 * Dos cosas distintas viven aquí:
 *  - El elemento `<audio>` principal, único, que reproduce las pistas de
 *    música. La UI controla `play()`, `pause()`, `currentTime`, etc.
 *  - Un pool de elementos `<audio>` para los efectos de sonido (SFX) del
 *    cassette: click de botón, ruido de scrub, click de stop, cambio de
 *    cinta. Se cargan en lazy: la primera vez que se piden.
 *
 * Si un archivo SFX no existe (404), los listeners de error lo silencian.
 * No queremos que un sonido faltante rompa la radio.
 */

/** IDs de SFX disponibles. */
export type SfxId = 'button' | 'fast' | 'stop' | 'insert';

/** Mapeo de SFX a sus archivos. Las rutas son absolutas (servidas desde public/). */
const SFX_FILES: Record<SfxId, string> = {
  button: '/sounds/Play01.ogg',
  fast: '/sounds/Fast.ogg',
  stop: '/sounds/Stop01.ogg',
  insert: '/sounds/Insert_Cartridge.ogg',
};

/** Pool lazy de audios SFX. Se rellena la primera vez que se pide cada uno. */
const sfxPool: Partial<Record<SfxId, HTMLAudioElement>> = {};

/** Volumen al que se reproducen los SFX (independiente del knob de música). */
const SFX_VOLUME = 0.7;

/** Obtiene (creando si hace falta) el elemento `<audio>` para un SFX. */
function getSfxAudio(id: SfxId): HTMLAudioElement {
  let a = sfxPool[id];
  if (a) return a;
  a = new Audio(SFX_FILES[id]);
  a.preload = 'auto';
  a.volume = SFX_VOLUME;
  // Silenciar errores: si el archivo no existe, no queremos romper la radio
  a.addEventListener('error', () => {
    /* archivo SFX no encontrado — ok */
  });
  sfxPool[id] = a;
  return a;
}

export interface PlaySfxOptions {
  /** Si true, reproduce en bucle. Usado para el ruido de scrub. */
  loop?: boolean;
}

/**
 * Reproduce un SFX. Si ya estaba sonando, lo reinicia desde el principio
 * (excepto en modo loop, donde lo deja seguir).
 */
export function playSfx(id: SfxId, opts: PlaySfxOptions = {}): void {
  const a = getSfxAudio(id);
  if (opts.loop) {
    a.loop = true;
    a.play().catch(() => {
      /* autoplay bloqueado, ok */
    });
  } else {
    a.loop = false;
    try {
      a.currentTime = 0;
    } catch {
      /* algunos navegadores tiran en estados raros */
    }
    a.play().catch(() => {
      /* autoplay bloqueado, ok */
    });
  }
}

/** Detiene un SFX (típicamente para parar el loop de scrub). */
export function stopSfx(id: SfxId): void {
  const a = sfxPool[id];
  if (!a) return;
  try {
    a.pause();
    a.currentTime = 0;
  } catch {
    /* idem */
  }
}

/** Detiene todos los SFX que estén sonando. Útil al cerrar el modal. */
export function stopAllSfx(): void {
  for (const id of Object.keys(sfxPool) as SfxId[]) {
    stopSfx(id);
  }
}

// ─── Audio principal (música) ────────────────────────────────────────────

/**
 * Elemento `<audio>` único para la música. Se exporta como singleton para
 * que la UI lo controle directamente (evita duplicar reproducciones).
 *
 * `preload = 'none'` evita que descargue la pista entera al instante; el
 * navegador la baja cuando se llama a `.play()`.
 */
export const musicAudio: HTMLAudioElement = new Audio();
musicAudio.preload = 'none';
